import type { AmbientScribeContext } from '../types';
import { FEATURE_FLAGS, type FeatureFlags } from '../types/featureFlags';
import type { FormattedTemplate } from '../types/templates';
import type { ContextDataItem } from '../types/widget';
import { shouldIncludeItem } from './buildStructuredContext';
import { isFormattedContextItem } from './formatContextData';
import { detectPipelineVersion } from './templateUtils';

/** Context item names reported as themselves; any other host key is `other`. */
const CONTEXT_FIELDS = [
  'previousNote',
  'currentSoapNote',
  'intake',
  'survey',
  'patientForms',
  'selfCheckInData',
  'patientDemographics',
  'freeTextContext',
] as const;

export type StartContextField = (typeof CONTEXT_FIELDS)[number] | 'other';

/**
 * What a session starts with, sent on session creation: names and metadata
 * only. Context values, patient data and template text are never included, and
 * a value outside the API's closed lists or patterns is sent as null.
 */
export interface StartSetup {
  schemaVersion: 1;
  template: {
    id: string | null;
    source: 'brand' | 'practice' | 'user' | 'host' | null;
    version: string | null;
  } | null;
  contextFields: StartContextField[];
  flagsOn: string[];
  hostBrand: string | null;
}

const ID_PATTERN = /^[A-Za-z0-9_-]{1,128}$/;
const VERSION_PATTERN = /^[A-Za-z0-9._-]{1,32}$/;
const BRAND_PATTERN = /^[a-z0-9-]{1,32}$/;

/**
 * How many included items of one context type the host sent, sent on every
 * generation request and template snapshot. Never a value, label or host key.
 */
export interface ContextItemCount {
  type: StartContextField;
  count: number;
}

const matching = (value: unknown, pattern: RegExp): string | null =>
  typeof value === 'string' && pattern.test(value) ? value : null;

const contextField = (key: ContextDataItem['key']): StartContextField =>
  (CONTEXT_FIELDS as readonly string[]).includes(String(key))
    ? (String(key) as StartContextField)
    : 'other';

function hasValue(item: ContextDataItem): boolean {
  if (item.children?.length) return item.children.some(isIncluded);
  return item.value !== undefined && item.value !== null && item.value !== '';
}

function isIncluded(item: ContextDataItem): boolean {
  return (item.hidden || Boolean(item.selected)) && hasValue(item);
}

export function buildStartSetup({
  template,
  contextData,
  features,
  brand,
}: {
  template: FormattedTemplate | undefined;
  contextData: ContextDataItem[];
  features: FeatureFlags | undefined;
  brand: string | undefined;
}): StartSetup {
  const present = new Set<StartContextField>();
  for (const item of contextData) {
    if (!isIncluded(item)) continue;
    present.add(contextField(item.key));
  }
  return {
    schemaVersion: 1,
    template: template
      ? {
          id: matching(template.id, ID_PATTERN),
          source: template.isHostProvided ? 'host' : (template.scope ?? null),
          version: matching(template.version, VERSION_PATTERN),
        }
      : null,
    contextFields: [...CONTEXT_FIELDS, 'other' as const].filter((name) =>
      present.has(name)
    ),
    flagsOn: Object.values(FEATURE_FLAGS).filter(
      (flag) => features?.[flag] === true
    ),
    hostBrand: matching(brand, BRAND_PATTERN),
  };
}

/** The context keys v2/v3 sends as unstructured text, selected or not. */
const UNSTRUCTURED_KEYS: readonly StartContextField[] = [
  'patientDemographics',
  'freeTextContext',
];

/** How many items of one context entry v2/v3 sends (see `buildContextForPipeline`). */
function countV2(
  item: ContextDataItem,
  contextData: ContextDataItem[]
): number {
  const type = contextField(item.key);
  if (UNSTRUCTURED_KEYS.includes(type)) {
    const first = contextData.find((candidate) => candidate.key === item.key);
    return first === item && typeof item.value === 'string' && item.value
      ? 1
      : 0;
  }
  if (!shouldIncludeItem(item)) return 0;
  if (item.children?.length) {
    return item.children.filter(
      (child) =>
        shouldIncludeItem(child) &&
        child.value !== undefined &&
        child.value !== null &&
        child.value !== ''
    ).length;
  }
  return item.value !== undefined && item.value !== null && item.value !== ''
    ? 1
    : 0;
}

/** How many items of one context entry v1 sends (see `formatContextData`). */
function countV1(item: ContextDataItem): number {
  return (
    (isFormattedContextItem(item) ? 1 : 0) +
    (item.children?.filter(isFormattedContextItem).length ?? 0)
  );
}

/**
 * The context items per type that the template's pipeline sends to the model,
 * in list order. Types and counts only, never a value, label or host key.
 */
export function buildContextItems(
  contextData: ContextDataItem[],
  template: FormattedTemplate | undefined
): ContextItemCount[] {
  const pipelineVersion = detectPipelineVersion(template?.template);
  const counts = new Map<StartContextField, number>();
  for (const item of contextData) {
    const count =
      pipelineVersion === 'v1' ? countV1(item) : countV2(item, contextData);
    if (count === 0) continue;
    const type = contextField(item.key);
    counts.set(type, (counts.get(type) ?? 0) + count);
  }
  return [...CONTEXT_FIELDS, 'other' as const]
    .filter((type) => counts.has(type))
    .map((type) => ({ type, count: counts.get(type)! }));
}

/**
 * The host's widget setup as booleans and one count, never label text. RUM
 * records it in `widgetConfig`; every generation request and template snapshot
 * also sends it, and the API checks it against the same keys.
 */
export interface WidgetConfigFlags {
  autoExport: {
    enabled: boolean;
    skipDocumentTabNavigation: boolean;
    autoCloseAfterExport: boolean;
    hideSoapNotesTab: boolean;
    flatTranscriptView: boolean;
    collapseTranscriptDuringGeneration: boolean;
  };
  ui: {
    hideTemplateDropdown: boolean;
    notesTabLabelOverridden: boolean;
    updateNoteButtonLabelOverridden: boolean;
    standalone: boolean;
    hasConfigurableMenuActions: boolean;
    templateDropdownActionCount: number;
  };
}

export const buildWidgetConfigFlags = (
  context: AmbientScribeContext | undefined
): WidgetConfigFlags => {
  const autoExport = context?.autoExportConfig;
  const notesTabLabel = context?.notesTabLabel;
  const updateNoteButtonLabel = context?.updateNoteButtonLabel;

  return {
    autoExport: {
      enabled: Boolean(autoExport?.autoExportAfterGeneration),
      skipDocumentTabNavigation: Boolean(autoExport?.skipDocumentTabNavigation),
      autoCloseAfterExport: Boolean(autoExport?.autoCloseAfterExport),
      hideSoapNotesTab: Boolean(autoExport?.hideSoapNotesTab),
      flatTranscriptView: Boolean(autoExport?.flatTranscriptView),
      collapseTranscriptDuringGeneration: Boolean(
        autoExport?.collapseTranscriptDuringGeneration
      ),
    },
    ui: {
      hideTemplateDropdown: Boolean(context?.hideTemplateDropdown),
      notesTabLabelOverridden:
        typeof notesTabLabel === 'string' && notesTabLabel.trim().length > 0,
      updateNoteButtonLabelOverridden:
        typeof updateNoteButtonLabel === 'string' &&
        updateNoteButtonLabel.trim().length > 0,
      standalone: Boolean(context?.standalone),
      hasConfigurableMenuActions: Boolean(context?.configurableMenuActions),
      templateDropdownActionCount:
        context?.templateDropdownActions?.length ?? 0,
    },
  };
};
